import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Vector3 } from "three";
import { InputActionDetail, InputActionEvent } from "./input/common";
import { AmmoGlobal, AmmoBodyComponent, AmmoPhysicsSystem, AmmoCollisionObject } from "./ammo";
import { RtcAvatarSystem } from "./rtc/rtc-avatar";
import { RtcEntityComponent, RtcEntitySystem } from "./rtc/rtc-entity";
import { SlotEvent } from "./slot";
import { ClipMap } from "./animation/clips";
import { Guid } from "../rtc/guid";

declare var Ammo : AmmoGlobal;
interface AvatarPhysicsSystem extends System
{
	avatar : AvatarComponentImpl;
}


export interface AvatarActionInstance
{
	//returns true is the action is still running, false if it ended and should be destroyed
	tick(avatar : AvatarComponent, t : number, dt : number, wasGrounded : boolean) : boolean | undefined | null;

	//called once when tick returned false
	destroy(avatar : AvatarComponent) : void;
}

export type AvatarActionFactory = ((avatar : AvatarComponent, input : InputActionDetail, source? : Entity) => AvatarActionInstance | null | undefined | false) & 
{
	disabled? : boolean;
	label? : string;
	icon? : string;
	cursor? : string;
};

export type AvatarEntity = Entity< { [attr:string] : Component } & {"avatar" : AvatarComponent} >;
export interface AvatarSystem extends System
{
	avatar : AvatarEntity;
	collisionReactions : { [ bodyPtr : string ] : CollisionReaction };
	actions : { [ key : string ] : AvatarActionFactory };
	data : {
		groundCollider : string[];
		skeletonRoot : string;
		idleAnimation : string;
		menuAnimation : string;
		walkAnimation : string;
		runAnimation : string;
		slowWalkAnimation : string;
		jumpAnimation : string;
		fallAnimation : string;
	};
	setAction( key : string, factory? : AvatarActionFactory | null ) : void;
}

export type AnimationDefinition = {
	clips : string[]; //What clips to play
	filter? : string; //On which objects
};
export type AnimationMap = { [ regex : string ] : AnimationDefinition };
export interface SetAnimationEventDetail
{
	level : number;
	animations : { [regex : string] : AnimationDefinition | string | string[] }; //Easier to write than AnimationDefinition
}


export type CollisionReaction = (phase : number, other? : AmmoCollisionObject, self? : AmmoCollisionObject) => void;

export interface AvatarComponent extends Component
{
	el : AvatarEntity;
	system : AvatarSystem;
	
	inputs : { source : Entity, detail : InputActionDetail}[];
	maxInput(action : string, threshold : number ) : InputActionDetail;

	velocity : Vector3;
	orientation : number;
	//grounded : boolean;

	groundContacts : AmmoBodyComponent[];

	animations : AnimationMap[];
	animation? : ClipMap;

	actions : { [action:string] : AvatarActionInstance }; //What the avatar is doing

	
	//The entities the avatar interacted with, and is responsible for until further notice.
	trackedEntities : { [body : string] : Entity & { components : { 
		"rtc-entity" : RtcEntityComponent,
		"ammo-body" : AmmoBodyComponent
	} } }; 

	data : {
		walkSpeed : number;
		runSpeed : number;
		jumpSpeed : number;
		freeOrientation : boolean;
	},

	rtc : RtcAvatarSystem;
	nextRtcPositionUpdate : number;
	forceRtcPositionUpdate() : void;


}


interface AvatarComponentImpl extends AvatarComponent
{
	prePhysicsTick(t : number, dt : number) : void;
	postPhysicsTick(t : number, dt : number) : void;
	handleCollisions(physicsSystem : AmmoPhysicsSystem, avatarBody : AmmoCollisionObject | false, xr : boolean ) : void;

	setAnimation( level : number, animation : AnimationMap ) : void;
	setDefaultAnimation( wasGrounded : boolean, desiredVelocity : { x:number, y:number, z:number } ) : void;
}


const emptyInputActionDetail : InputActionDetail = 
{
	action : "",
	timestamp : NaN,
	amount : 0,
	getRay : () => {},
	feedback : (k) => {}
};


const lerp = (a:number, b:number, k:number) => a * (1.0 - k) + b * k;


registerSystem("avatar-pre-physics-system", {
	tick(this:AvatarPhysicsSystem, t:number, dt:number)
	{
		this.avatar && this.avatar.prePhysicsTick(t, dt);
	}
});

registerSystem("avatar-post-physics-system", {
	tick(this : AvatarPhysicsSystem, t:number, dt:number)
	{
		this.avatar && this.avatar.postPhysicsTick(t, dt);
	}
});


registerSystem("avatar", {
	schema : {
		groundCollider : { type : "array" },
		skeletonRoot : { type : "string", default : "Hips" },

		idleAnimation : { type : "string", default : "Idle" },
		menuAnimation : { type : "string", default : "Menu" },
		walkAnimation : { type : "string", default : "Walk" },
		runAnimation : { type : "string", default : "Run" },
		slowWalkAnimation : { type : "string", default : "SlowWalk" },
		jumpAnimation : { type : "string", default : "Jump" },
		fallAnimation : { type : "string", default : "Fall" }
	},

	init(this : AvatarSystem)
	{
		//Put avatar-pre-physics-system before physics (and any other system from this module)
		const names = this.el.systemNames;
		this.el.systemNames.splice( names.indexOf("avatar-pre-physics-system"), 1 );
		this.el.systemNames.splice( names.indexOf("physics"), 0, "avatar-pre-physics-system" );

		const builtInAction = () => null;
		this.actions = {
			"Jump" : builtInAction,
			"Run" : builtInAction,
			"MoveForward" : builtInAction,
			"MoveBackward" : builtInAction,
			"MoveLeft" : builtInAction,
			"MoveRight" : builtInAction
		};

		this.collisionReactions = {};
	},

	setAction(this : AvatarSystem, key : string, factory? : AvatarActionFactory | null)
	{
		if(factory)
			this.actions[key] = factory;
		else
			delete this.actions[key];
	}
} );




const sameAnimations = (a : AnimationMap, b : AnimationMap) =>
{
	for(let k in a)
	{
		if(!(k in b))
			return false;
		const av = a[k], bv = b[k];
		if(av.filter != bv.filter)
			return false;
		let n = av.clips.length;
		if(n != bv.clips.length)
			return false;
		while(--n >= 0)
			if(av.clips[n] != bv.clips[n])
				return false;
	}
	for(let k in b)
		if(!(k in a))
			return false;
	return true;
}

const cloneAnimations = (anim : AnimationMap) =>
{
	const copy : AnimationMap = {};
	for(let k in anim)
	{
		const src = anim[k];
		copy[k] = {
			filter : src.filter,
			clips : src.clips.slice()
		};
	}
	return copy;
};



const movementDirections : { angle : number, suffix : string, prefix? : boolean }[] = [
	{
		angle : -(Math.SQRT2 + 1),
		suffix : "Back"
	},
	{
		angle : -(Math.SQRT2 - 1),
		suffix : "BackRight",
		prefix : true
	},
	{
		angle : (Math.SQRT2 - 1),
		suffix : "Right",
		prefix : true
	},
	{
		angle : (Math.SQRT2 + 1),
		suffix : "FrontRight",
		prefix : true
	}
];



registerComponent("avatar-inventory", {
	init(this : Component)
	{
		this.el.object3D.visible = false;

		this.el.addEventListener("spawn-entity", async (event) =>
		{
			const detail = (event as CustomEvent).detail as {
				template : Guid | string
			};

			const entitySystem = this.el.sceneEl!.systems["rtc-entity"] as RtcEntitySystem;
			entitySystem.createEntityAsync( detail.template, undefined, { transform : {}, attach : {} } );
		});
	}
});


registerComponent("avatar", {

	schema : {
		walkSpeed : { type :"number", default : 3 },
		runSpeed : { type :"number", default : 6 },
		jumpSpeed : { type :"number", default : 3 },
		freeOrientation : { type : "boolean", default : true } // 
	},
	
	init(this : AvatarComponentImpl)
	{
		const el = this.el;
		const sceneEl = el.sceneEl!, obj = el.object3D;

		this.system.avatar = el;
		(sceneEl.systems["avatar-pre-physics-system"] as AvatarPhysicsSystem).avatar = this;
		(sceneEl.systems["avatar-post-physics-system"] as AvatarPhysicsSystem).avatar = this;

		if(el.isPlaying) el.emit("avatar-loaded", this, true);
		else el.addEventListener("play", () => el.emit("avatar-loaded", this, true), { once:true });

		//Don't need to care about UI : Modal UI shuts down input events
		this.inputs = [];
		const inputBeginListener : EventListener = ((e:InputActionEvent) => { 
			if(e.defaultPrevented) return;
			this.inputs.push( { source : e.target as Entity, detail : e.detail } ); 
		}) as EventListener;
		const inputEndListener : EventListener = ((e:InputActionEvent) => {
			for(let ix = this.inputs.length; --ix >= 0; )
				if(this.inputs[ix].detail === e.detail)
					this.inputs.splice(ix, 1);
		}) as EventListener;
		el.addEventListener("input-begin", inputBeginListener );
		el.addEventListener("input-end", inputEndListener );

		//Disable input listener in UI mode :
		sceneEl.addEventListener("enter-ui", () => el.removeEventListener("input-begin", inputBeginListener ) );
		sceneEl.addEventListener("exit-ui", () => el.addEventListener("input-begin", inputBeginListener ) );



		this.actions = {};
		this.groundContacts = [];
		this.velocity = new Vector3();
		this.orientation = 0.0;
		this.el.object3D.rotation.order = 'YXZ';
		this.nextRtcPositionUpdate = 0;
		this.trackedEntities = {};


		//Track rtc entites on attach (colliding other entities) and on detach (tracking the fall and deactivation)
		const onSlotEvent = (e : SlotEvent) => 
		{
			const item = e.detail.item;
			const rtcEC = item.components["rtc-entity"] as RtcEntityComponent;
			const bodyC = item.components["ammo-body"] as AmmoBodyComponent;
			rtcEC && bodyC && bodyC.bodyInitialized.then( (rb) =>
				this.trackedEntities[ Ammo.getPointer(rb) ] = item as any
			);
		}
		el.addEventListener("item-attached", onSlotEvent as EventListener);
		el.addEventListener("item-detached", onSlotEvent as EventListener);
		

		this.animations = [ { "" : { clips : [ "Idle" ] } } ];
		el.addEventListener("set-animation", (e) =>
		{
			const detail = (e as DetailEvent<SetAnimationEventDetail>).detail;
			const animations : AnimationMap = {};
			for(let key in detail.animations)
			{
				const value = detail.animations[key];
				if(typeof value === "string")
					animations[key] = { clips : [ value ]};
				else if(Array.isArray(value))
					animations[key] = { clips : value };
				else
					animations[key] = value;
			}
			return this.setAnimation(detail.level, animations);
		} );



		const rtc = sceneEl.systems["rtc-avatar"] as RtcAvatarSystem;
		rtc && sceneEl.addEventListener("rtc-connected", (event) =>
		{
			this.rtc = rtc;
			rtc.sendLocalAvatarPosition(obj.position, this.orientation, this.velocity, this.groundContacts.length > 0);
			rtc.sendLocalAvatarAnimation(this.animation || { "" : [] } );

			const events = rtc.data.events || [];
			const transmitEvent = (event : CustomEvent) => rtc.sendEvent(event.type, event.detail);
			for(let event of events)
				el.addEventListener(event, transmitEvent as EventListener);
		});
	},

	forceRtcPositionUpdate(this:AvatarComponentImpl) : void
	{
		this.nextRtcPositionUpdate = -1;
	},




	/*

	level 0 :
	1 x Global body animation 
		=> Idle, Walk, Run, Jump, Fall

	level 1 : 
	2+ x Slot animation
		=> * : fingers closed
		=> Idle / other : allow arm or body animation

	level 2 :
	2 x Action animation
		=> Idle : whole animation
		=> * : only with acting arm / upper body

	level 3 :
	Input animation
		=> Reflect fingers on VR device
	*/

	setAnimation(this:AvatarComponentImpl, level : number, animations : AnimationMap) : void
	{
		const previousValue = this.animations[level] || {};
		if(sameAnimations(previousValue, animations))
			return; //No change

		this.animations[level] = cloneAnimations(animations);

		let mainClips = this.animations[0][""].clips.join(",");
				
		const result : ClipMap = {};
		for(let index in this.animations)
		{
			if(index == "0") continue;
			
			const animSet = this.animations[index];
			for(let key in animSet)
			{
				const re = new RegExp(key);
				if(!re.test(mainClips))
					continue;

				const anim = animSet[key];
				if(anim.filter) //Play on a subset
					result[ anim.filter ] = anim.clips;
				else //Play on whole body == mainClips
					mainClips = anim.clips.join(",");
				break;
			}
		}

		result[""] = mainClips.split(",");

		this.animation = result;
		this.el.setAttribute("clips", result);
		this.rtc && this.rtc.sendLocalAvatarAnimation(result);
	},
	
	maxInput(this : AvatarComponent, action : string, threshold : number ) : InputActionDetail
	{
		let max : InputActionDetail = emptyInputActionDetail;
		for(let input of this.inputs)
		{
			const i = input.detail;
			if((i.action == action) && (i.amount >= threshold) && (i.amount > max.amount))
				max = i;
		}
		return max;
	},

	prePhysicsTick(this : AvatarComponentImpl, t : number, dt : number)
	{
		const el = this.el;
		const scene = el.sceneEl!;
		const cam = scene.camera.matrixWorld.elements;

		const xr = scene.renderer.xr;
		if(xr && xr.enabled && xr.isPresenting)
		{
			el.object3D.position.x = cam[12];
			el.object3D.position.z = cam[14];

			const kXy = Math.abs(cam[1]);
			const ikXy = 1.0 - kXy;
			let doiX = cam[2] * ikXy - kXy * cam[8], doiZ = -cam[0] * ikXy - kXy * cam[10];

			// TODO : make this work...
			/*
			const k = 1.0 / Math.sqrt(doiX * doiX + doiZ * doiZ);
			doiX /= k; doiZ /= k;

			const xrSession = xr.getSession(), xrFrame = xr.getFrame(), xrSpace = xr.getReferenceSpace();
			const vrRoot = scene.camera.el.object3D.parent;
			for(let c = (xrSession && xrFrame && xrSpace) ? xrSession.inputSources.length : 0; --c >= 0; )
			{
				const src = xrSession!.inputSources[c];
				if(!src.gripSpace) continue;
				const pose = xrFrame.getPose(src.gripSpace, xrSpace!);
				if(!pose) continue;

				const V = (new Vector3()).copy(pose.transform.position);
				vrRoot && V.applyMatrix4(vrRoot.matrixWorld);
				V.x -= cam[12];
				V.z -= cam[14];
				if( (V.x * doiX + V.z * doiZ) <= 0 ) continue;
				V.y = 0;
				V.normalize();
				doiX += V.x;
				doiZ += V.z;
			}
			*/

			this.orientation = Math.atan2( doiX, doiZ );
			el.object3D.rotation.y = this.orientation;
			el.object3D.updateWorldMatrix(false, true);
			return;
		}

		//Handle base movement inputs :
		let velX = 0.0, velZ = 0.0;

		if( el.parentEl === el.parentElement ) //Avatar is not attached to some other entity (typically a chair)
		{
			velX = this.maxInput("MoveRight", 0.05).amount - this.maxInput("MoveLeft", 0.05).amount;
			velZ = this.maxInput("MoveBackward", 0.05).amount - this.maxInput("MoveForward", 0.05).amount;
			
			//Free move action (direction from input ray)
			for(let input of this.inputs)
			{
				const detail = input.detail;
				if(detail.action != "Move") continue;
				
				detail.getRay( 0, detail.amount, 
					function(this: number, x : number, y : number, z : number)
					{
						if(this <= 0) return;
						const k = this / Math.sqrt(x*x + z*z);
						velX += k * (x * cam[0] + z * cam[2]);
						velZ += k * (z * cam[0] - x * cam[2]);
					} );
			}

			if(velX && velZ)
			{
				const N2 = velX*velX + velZ*velZ;
				if(N2 > 1.0)
				{
					const q = 1.0 / Math.sqrt(N2);
					velX *= q; velZ *= q;
				}
			}
		
			if(velX || velZ) //Kevin wants to move
			{
				const speed = lerp(this.data.walkSpeed, this.data.runSpeed, this.maxInput("Run", 0).amount );
				velX *= speed;
				velZ *= speed;

				const Xx = cam[0], Xz = cam[2], Zx = -cam[2], Zz = cam[0];
				velX && (velX /= Math.sqrt(Xx*Xx + Xz*Xz));
				velZ && (velZ /= Math.sqrt(Zx*Zx + Zz*Zz));
				this.velocity.x = velX * Xx + velZ * Zx;
				this.velocity.z = velX * Xz + velZ * Zz;

				if(this.data.freeOrientation) //Orientation follows desired movement (= velocity in prePhysicsTick)
					this.orientation = Math.atan2( this.velocity.x, this.velocity.z );
				else //Orientation follows camera
					this.orientation = Math.atan2( -Zx, -Zz );
			}
			else
			{
				this.velocity.x = this.velocity.z = 0.0;
				if(!this.data.freeOrientation) //TODO : threshold
					this.orientation = Math.atan2( cam[2], -cam[0] );
			}
		}
		else
		{
			this.orientation = 0.0;
			el.object3D.rotation.y = 0.0;
		}
		

		const ammoBody = el.components["ammo-body"] as AmmoBodyComponent;
		const avatarBody = ammoBody && ammoBody.data && ammoBody.data.enabled && (ammoBody.data.type == 'dynamic') && ammoBody.body;
		if( avatarBody ) //Enabled, dynamic physics body
		{
			if(this.groundContacts.length > 0)
				this.velocity.y = this.maxInput("Jump", 0.5).amount * this.data.jumpSpeed;
			else
				this.velocity.y += dt * 0.001 * (this.el.sceneEl!.systems.physics?.data?.gravity ?? -9.8);

			const V = avatarBody.getLinearVelocity();
			V.setX(this.velocity.x);
			V.setZ(this.velocity.z);
			(this.groundContacts.length > 0) && (this.velocity.y > 0) && V.setY(this.velocity.y);
			avatarBody.activate();
		}
		else
			this.velocity.y = 0.0;
	},


	handleCollisions( this : AvatarComponentImpl, physicsSystem : AmmoPhysicsSystem, avatarBody : AmmoCollisionObject, xr : boolean )
	{
		const dispatcher = physicsSystem.driver.dispatcher;
		if(!dispatcher)
			return; //Not running yet.

		const reactions : { [body:string] : CollisionReaction } = {};
		const phase1 : CollisionReaction[] = [];

		for(let bodyPtr in this.system.collisionReactions)
		{
			const cr = this.system.collisionReactions[ bodyPtr ];
			const bc = physicsSystem.components[ bodyPtr ];
			//Only react to existing, initialized and enabled bodies
			if(!(bc && bc.data && bc.data.enabled))
				continue;
			cr(-1);
			reactions[ bodyPtr ] = cr;
			phase1.push(cr);
		}
			
			
		const entityReaction = (phase : number, other? : AmmoCollisionObject) =>
		{
			const ptr = Ammo.getPointer(other);
			const otherBodyComponent = physicsSystem.components[ ptr ];
			if(!otherBodyComponent) return;
			const rtcEntity = otherBodyComponent.el.components["rtc-entity"] as RtcEntityComponent;
			if(!rtcEntity) return;
			if(otherBodyComponent.data.type != "dynamic" ) return;
			this.trackedEntities[ ptr ] = otherBodyComponent.el as any;
			rtcEntity.sendTransform();
		};

		if(avatarBody)
			reactions[ Ammo.getPointer(avatarBody) ] = entityReaction;

		const deactivatedEntities = [];
		for(let entityPtr in this.trackedEntities)
		{
			const entity = this.trackedEntities[ entityPtr ];
			const entityBody = entity.components["ammo-body"];
			if(entityBody && entityBody.body && entityBody.body.isActive() && entityBody.data && (entityBody.data.type == "dynamic"))
				reactions[ entityPtr ] = entityReaction;
			else
			{
				console.log("RtcEntity deactivated, sending final transform.");
				//Entity stopped its movement : send final transform and forget about it.
				entity.components["rtc-entity"].sendTransform();
				deactivatedEntities.push(entityPtr);
			}
		}
		for(let entityPtr of deactivatedEntities)
			delete this.trackedEntities[entityPtr];


		const previousGroundContacts = this.groundContacts.splice(0, this.groundContacts.length);

		if(this.velocity.y <= 0) //Not going up
		{
			const groundReaction = (phase : number, other? : AmmoCollisionObject) => 
			{
				const ptr = Ammo.getPointer(other);
				const otherBodyComponent = physicsSystem.components[ ptr ];
				if(!otherBodyComponent) return;

				const ix = previousGroundContacts.indexOf(otherBodyComponent);
				if(ix >= 0)
					previousGroundContacts.splice(ix, 1);
				else
					otherBodyComponent.el.emit("enter-ground-contact");

				this.groundContacts.push(otherBodyComponent);
			}

			for(let bodyAttr of this.system.data.groundCollider)
			{
				const ammoBody = this.el.components[bodyAttr] as AmmoBodyComponent;
				if(ammoBody && ammoBody.body)
					reactions[ Ammo.getPointer(ammoBody.body) ] = groundReaction;
			}
		}

		for(let m = dispatcher.getNumManifolds(); --m >= 0; )
		{
			const manifold = dispatcher.getManifoldByIndexInternal(m);
			if(manifold.getNumContacts() == 0) continue;

			const one = manifold.getBody0(), other = manifold.getBody1();
			const oneR = reactions[ Ammo.getPointer(one) ], otherR = reactions[ Ammo.getPointer(other) ];
			oneR && oneR(0, other, one);
			otherR && otherR(0, one, other);
		}

		for(let otherBodyComponent of previousGroundContacts)
			otherBodyComponent.el.emit("exit-ground-contact");

		for(let cr of phase1)
			cr(1);
	},

	postPhysicsTick( this:AvatarComponentImpl, t : number, dt : number )
	{
		const el = this.el;
		const sceneEl = el.sceneEl!;
		const now = Date.now(), rtcSystem = sceneEl.systems["rtc-avatar"] as RtcAvatarSystem;
		const xr = sceneEl.renderer.xr;

		const xrMode = xr && xr.enabled && xr.isPresenting;

		if(xrMode)
		{
			if(rtcSystem && rtcSystem.client && (now >= this.nextRtcPositionUpdate))
			{
				this.nextRtcPositionUpdate = now + 50; //sync at 20Hz
				rtcSystem.sendLocalAvatarPosition(el.object3D.position, this.orientation, this.velocity, true );
				for(let key in el.object3DMap)
				{
					const hips = el.object3DMap[key].getObjectByName(this.system.data.skeletonRoot);
					if(!hips)
						continue;

					rtcSystem.sendLocalAvatarSkeleton(el, hips);
					break;
				}
			}
		}


		sceneEl.camera.updateWorldMatrix(true, false);
		
		const physicsSystem = sceneEl.systems.physics as AmmoPhysicsSystem;
		const ammoBody = el.components["ammo-body"] as AmmoBodyComponent;
		const avatarBody = ammoBody && ammoBody.data && ammoBody.data.enabled && (ammoBody.data.type == 'dynamic') && ammoBody.body;
	
		const wasGrounded = xrMode || !avatarBody || (this.groundContacts.length > 0);
		
		if(physicsSystem)
			this.handleCollisions(physicsSystem, avatarBody, xrMode);

		const isGrounded = xrMode || !avatarBody || (this.groundContacts.length > 0);

		const desiredVelocity = { x : this.velocity.x, y : this.velocity.y, z : this.velocity.z };

		if(avatarBody && !xrMode)
		{
			const V = avatarBody.getLinearVelocity();
			const velX = V.x(), velZ = V.z(); let velY = 0.0;
			if( !this.groundContacts.length )
			{
				velY = V.y();
				if( this.velocity.y < velY ) //Sometimes the physics body stops falling while it shouldn't. (i.e. jumping towards a wall)
					V.setY( velY = this.velocity.y );
			}
			this.velocity.set(velX, velY, velZ);
		}


		//First, update active actions. 
		//Some may decide to stop.
		const actionsDone = [];
		for(let key in this.actions)
			if(!this.actions[key].tick(this, t, dt, wasGrounded))
				actionsDone.push(key);
		for(let key of actionsDone)
		{
			this.actions[key].destroy(this);
			delete this.actions[key];
		}

		//Next, try to start new actions.
		//This allows to restart new actions if they just stopped.
		for(let input of this.inputs)
		{
			const i = input.detail;
			//TODO : handle the same action from multiple devices
			if(i.action in this.actions)
				continue;
			const factory = this.system.actions[ i.action ];
			if(!factory || factory.disabled)
				continue;
			const instance = factory(this, i, input.source);
			if(!instance)
				continue; //Factory decided the action can't start.	
			if(instance.tick(this, t, dt, wasGrounded))
				this.actions[ i.action ] = instance;
			else
				instance.destroy(this);
		}

		// Set the core animation whatever the actions to reflect physics
		if( el.parentEl === el.parentElement )
			this.setDefaultAnimation(wasGrounded, desiredVelocity);

		if(!xrMode)
			el.object3D.rotation.y = this.orientation;
		
		if(rtcSystem && rtcSystem.client && (now >= this.nextRtcPositionUpdate))
		{
			this.nextRtcPositionUpdate = now + 50; //sync at 20Hz
			rtcSystem.sendLocalAvatarPosition(el.object3D.position, this.orientation, this.velocity, isGrounded);
		}


		//Eliminate instant inputs : 
		for(let ix = this.inputs.length; --ix >= 0; )
			if(this.inputs[ix].detail.instant)
				this.inputs.splice(ix, 1);
	},

	setDefaultAnimation(this : AvatarComponentImpl, wasGrounded : boolean, desiredVelocity : { x:number, y:number, z:number }) : void
	{
		const systemData = this.system.data;

		if(this.groundContacts.length > 0)
		{
			let prefix = "", suffix = "";

			const sqSpd = this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z;
			if( (sqSpd > 0.01) && !this.data.freeOrientation )
			{
				const u = Math.sin(this.orientation), v = Math.cos(this.orientation);
				const p =  u * desiredVelocity.x + v * desiredVelocity.z;
				const q =  u * desiredVelocity.z - v * desiredVelocity.x;

				const aq = (q != 0) ? Math.abs(q) : (0.001 * Math.abs(p));
				for(let md of movementDirections)
					if(p < (md.angle * aq))
					{
						suffix = md.suffix;
						if(md.prefix && (q < 0)) prefix = "~";
						break;
					} 
			}


			const sqRatio = sqSpd / (this.data.walkSpeed * this.data.walkSpeed);
			
			if( sqRatio > 1.1 ) // strictly greater than walk speed
				this.setAnimation(0, { "" : { clips : [prefix + systemData.runAnimation + suffix] } } );
			else if( sqRatio > 0.25 ) // more than 50% of full walk speed
				this.setAnimation(0, { "" : { clips : [prefix + systemData.walkAnimation + suffix] } } );
			else if( sqRatio > 0.001 ) // more than 3% of full walk speed
				this.setAnimation(0, { "" : { clips : [prefix + systemData.slowWalkAnimation + suffix] } } );
			else if( this.system.el.is("ui-mode"))
				this.setAnimation(0, { "" : { clips : [systemData.menuAnimation] } } );
			else
				this.setAnimation(0, { "" : { clips : [systemData.idleAnimation] } } );
		}
		else if( wasGrounded && (this.velocity.y > 0) && (desiredVelocity.y > 0) ) //Left the ground + going up + wanted to jump
		{
			this.setAnimation(0, { "" : { clips : [systemData.jumpAnimation, systemData.fallAnimation] } } );
		}
		else if(this.velocity.y < -0.5) //Falling fast enough
		{
			this.setAnimation(0, { "" : { clips : [systemData.fallAnimation] } } );
		}
		//else, don't change, whatever it is.
	}
})
